import { STORYGRAPH_INLINE_LINK_STYLE } from '../../constants'
import { getUserOptions, type UserOptions } from '../settings'
import { createLinkPill } from '../../utils/dom'

function extractBookPath (anchor: HTMLAnchorElement): string {
  const href = anchor.getAttribute('href') || ''
  const match = href.match(/\/books\/[^/?#]+/)
  return match?.[0] || ''
}

function toReviewEditHref (href: string): string {
  if (!href) return href

  let url: URL
  try {
    url = new URL(href, window.location.origin)
  } catch {
    return href
  }

  const match = url.pathname.match(/^\/reviews\/([^/?#]+)(?:\/edit)?\/?$/)
  if (!match) return href

  const [, reviewId] = match
  url.pathname = `/reviews/${reviewId}/edit`

  return url.origin === window.location.origin
    ? `${url.pathname}${url.search}${url.hash}`
    : url.toString()
}

function updateExistingReviewLinks (): void {
  const reviewLinks = Array.from(document.querySelectorAll<HTMLAnchorElement>('a[href*="/reviews/"]'))

  for (const link of reviewLinks) {
    const normalized = link.textContent?.trim().toLowerCase() || ''
    if (!normalized || normalized.includes('add review')) continue

    const href = link.getAttribute('href') || ''
    const editHref = toReviewEditHref(href)
    if (editHref && editHref !== href) {
      link.setAttribute('href', editHref)
    }

    if (normalized !== 'edit review') {
      link.textContent = 'edit review'
    }
  }
}

function extractIsbnFromCard (cardContainer: HTMLElement): string {
  const text = cardContainer.innerText || ''
  const isbnMatch = text.match(/\b(?:97[89][-\s]?)?\d(?:[-\s]?\d){8,15}\b/)
  if (!isbnMatch) return ''

  const normalized = isbnMatch[0].replace(/[-\s]/g, '')
  if (normalized.length !== 10 && normalized.length !== 13) return ''

  return normalized
}

function buildDoubanSearchHref (query: string): string {
  const encoded = encodeURIComponent(query)
  return `https://search.douban.com/book/subject_search?search_text=${encoded}&cat=1001`
}

function injectBookLinks (h3: HTMLHeadingElement, options: UserOptions): void {
  if (h3.hasAttribute('data-sg-enhanced')) return

  const titleLink = h3.querySelector<HTMLAnchorElement>('a[href*="/books/"]')
  if (!titleLink) return

  const bookPath = extractBookPath(titleLink)
  if (!bookPath) return

  if (!isBookCoverUnavailable(h3, bookPath)) {
    h3.setAttribute('data-sg-enhanced', 'true')
    return
  }

  const base = window.location.origin
  const metadataLink = createLinkPill('add metadata', `${base}${bookPath}/add_missing_info`, STORYGRAPH_INLINE_LINK_STYLE)

  const title = titleLink.textContent?.trim() || ''
  const cardContainer = h3.closest<HTMLElement>('.grid, article, li, tr, section')
  const isbn = cardContainer ? extractIsbnFromCard(cardContainer) : ''
  const doubanQuery = isbn || title

  titleLink.after(metadataLink)

  if (options.showDoubanSearchButton && doubanQuery) {
    const doubanSearchLink = createLinkPill('search douban', buildDoubanSearchHref(doubanQuery), STORYGRAPH_INLINE_LINK_STYLE)
    metadataLink.after(doubanSearchLink)
  }

  h3.setAttribute('data-sg-enhanced', 'true')
}

function isBookCoverUnavailable (h3: HTMLHeadingElement, bookPath: string): boolean {
  const cardContainer = h3.closest<HTMLElement>('.grid, article, li, tr, section')
  if (!cardContainer) return false

  const coverColumn = cardContainer.querySelector<HTMLElement>('.cover-image-column') || cardContainer
  const coverWrapper = coverColumn.querySelector<HTMLElement>('.book-cover, .placeholder-cover')
  const coverImage = coverColumn.querySelector<HTMLImageElement>(`a[href*="${bookPath}"] img`) || coverColumn.querySelector<HTMLImageElement>('img')

  const wrapperText = `${coverWrapper?.className || ''}`.toLowerCase()
  if (wrapperText.includes('placeholder-cover')) return true

  if (!coverImage) return true

  const sourceText = `${coverImage.currentSrc} ${coverImage.src} ${coverImage.srcset} ${coverImage.alt} ${coverImage.className}`.toLowerCase()
  const placeholderMarkers = ['placeholder-cover', 'placeholder', 'missing', 'no_cover', 'default_cover', 'book-cover-empty']

  return placeholderMarkers.some(marker => sourceText.includes(marker))
}

export function injectStoryGraphShortcuts (): void {
  const options = getUserOptions()

  if (options.convertReviewLinks) {
    updateExistingReviewLinks()
  }

  const headers = document.querySelectorAll<HTMLHeadingElement>('h3')
  headers.forEach((header) => injectBookLinks(header, options))
}
